// Logging Setup
import { LoggerProvider, BatchLogRecordProcessor } from '@opentelemetry/sdk-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { logs } from '@opentelemetry/api-logs';

// Tracing Setup
import customSpanProcessor from './custom-processor';
import { TracerProvider, BatchSpanProcessor } from '@opentelemetry/sdk-trace';
import { context, propagation, trace } from '@opentelemetry/api';
import { CompositePropagator, W3CBaggagePropagator, W3CTraceContextPropagator } from '@opentelemetry/core';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { ZoneContextManager } from '@opentelemetry/context-zone';

// Metrics Setup
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { metrics } from '@opentelemetry/api';

// Shared
import { resourceFromAttributes } from '@opentelemetry/resources';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { appEnv } from './env';

// Resource configuration
const resource = resourceFromAttributes({
  'service.name': appEnv.VITE_OTEL_SERVICE_NAME,
  'service.version': appEnv.VITE_OTEL_SERVICE_VERSION,
  'deployment.environment': appEnv.VITE_OTEL_DEPLOYMENT_ENVIRONMENT
});

// ============================================================================
// Logging Configuration
// ============================================================================

const loggerProvider = new LoggerProvider({
  resource,
  processors: [
    new BatchLogRecordProcessor({
      exporter: new OTLPLogExporter({
        url: `${appEnv.VITE_OTEL_EXPORTER_OTLP_ENDPOINT}/v1/logs`
      })
    })
  ]
});

logs.setGlobalLoggerProvider(loggerProvider);

// ============================================================================
// Tracing Configuration
// ============================================================================

const traceExporter = new OTLPTraceExporter({
  url: `${appEnv.VITE_OTEL_EXPORTER_OTLP_ENDPOINT}/v1/traces`
});

const tracerProvider = new TracerProvider({
  resource,
  spanProcessors: [new BatchSpanProcessor({ exporter: traceExporter }), customSpanProcessor]
});

// SDK 3 removed WebTracerProvider.register(); set up what it registered explicitly:
// the Zone context manager, the default W3C trace-context + baggage propagator,
// and the global tracer provider.
context.setGlobalContextManager(new ZoneContextManager().enable());
propagation.setGlobalPropagator(
  new CompositePropagator({ propagators: [new W3CTraceContextPropagator(), new W3CBaggagePropagator()] })
);
trace.setGlobalTracerProvider(tracerProvider);

const ignoreUrls = [
  /api\.iconify\.design/,
  /api\.simplesvg\.com/,
  /api\.unisvg\.com/,
  /fonts\.googleapis\.com/,
  /fonts\.gstatic\.com/,
  /cdn\./
];

registerInstrumentations({
  instrumentations: [
    // new FetchInstrumentation({
    //   ignoreUrls: ignoreUrls,
    //   propagateTraceHeaderCorsUrls: /.*/,
    //   clearTimingResources: true,
    // }),
    // new XMLHttpRequestInstrumentation({
    //   ignoreUrls: ignoreUrls
    //   propagateTraceHeaderCorsUrls: /.*/,
    //   clearTimingResources: true
    // })
    // new UserInteractionInstrumentation({
    //   eventNames: ['click', 'input', 'submit'],
    // })
    // new DocumentLoadInstrumentation({})
  ]
});

// ============================================================================
// Metrics Configuration
// ============================================================================

// Create a metric reader with OTLP exporter configured to send metrics to a local collector.
const metricReader = new PeriodicExportingMetricReader({
  exporter: new OTLPMetricExporter({
    url: `${appEnv.VITE_OTEL_EXPORTER_OTLP_ENDPOINT}/v1/metrics`
  }),
  exportIntervalMillis: 10000 // Export metrics every 10 seconds.
});

// Initialize a MeterProvider with the above configurations.
const myServiceMeterProvider = new MeterProvider({
  resource,
  readers: [metricReader]
});

// Set the initialized MeterProvider as global to enable metric collection across the app.
metrics.setGlobalMeterProvider(myServiceMeterProvider);
